import {createRouter, createWebHistory} from 'vue-router';
import NotFoundView from '@/views/NotFoundView.vue';
import SecureAreaLayout from "@/layouts/SecureAreaLayout.vue";
import PublicLayout from '@/layouts/PublicLayout.vue';
import KioskModeLayout from "@/layouts/KioskModeLayout.vue";
import { useAuthStore } from '@/stores/auth';
import { usePostHog } from '@/composables/usePostHog'

const routes = [
    // Health check – NO layout applied
    {
        path: '/health',
        name: 'health',
        component: () => import('@/views/HealthCheck.vue'),
        meta: { requiresAuth: false }
    },
    {
        path: '/',
        component: () => import('@/layouts/PublicLayout.vue'), // Use PublicLayout as the "master page" for public routes
        meta: { requiresAuth: false, isPublicParent: true },
        children: [
            {
                path: '', // Empty path matches '/' exactly
                name: 'home',
                component: () => import('@/views/HomeView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'product',
                name: 'product',
                component: () => import('@/views/ProductView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'price',
                name: 'price',
                component: () => import('@/views/PricingView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'contact-us',
                name: 'contact-us',
                component: () => import('@/views/ContactUsView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'kiosk-mode-login',
                name: 'kiosk-mode-login',
                component: () => import('@/views/KioskModeLoginView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'signup',
                name: 'signup',
                component: () => import('@/views/SignUpView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'login',
                name: 'login',
                component: () => import('@/views/LoginView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'terms-of-service',
                name: 'terms-of-service',
                component: () => import('@/views/TermsOfServiceView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'privacy-policy',
                name: 'privacy-policy',
                component: () => import('@/views/PrivacyPolicyView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'refund-policy',
                name: 'refund-policy',
                component: () => import('@/views/RefundPolicyView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'no-access',
                name: 'no-access',
                component: () => import('@/views/NoAccessView.vue'),
                meta: { requiresAuth: false }
            }
        ],
    },
    {
        path: '/kiosk',
        component: KioskModeLayout,
        children: [
            {
                path: 'kiosk-scan',
                name: 'kiosk-scan',
                component: () => import('@/views/kiosk/KioskScanView.vue'),
                meta: { requiresAuth: true, requiredUserType: ['Kiosk'] }
            },
            {
                path: 'invalid-access-level',
                name: 'kiosk-invalid-access-level',
                component: () => import('@/views/kiosk/KioskInvalidAccessLevelView.vue'),
                meta: { requiresAuth: false }
            }
        ]
    },
    {
        path: '/portal',
        component: SecureAreaLayout,
        children: [
            {
                path: 'invalid-access-level',
                name: 'invalid-access-level',
                component: () => import('@/views/portal/InvalidAccessLevelView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'employee-details/:id',
                name: 'employee-details',
                component: () => import('@/views/portal/EmployeeDetailsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'attributes',
                name: 'attributes',
                component: () => import('@/views/portal/AttributesView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'dashboard',
                name: 'dashboard',
                component: () => import('@/views/portal/DashboardView.vue'),
                meta: {requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'banned-accounts',
                name: 'banned-accounts',
                component: () => import('@/views/portal/BannedAccountsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'certifications',
                name: 'certifications',
                component: () => import('@/views/portal/CertificationsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'locations',
                name: 'locations',
                component: () => import('@/views/portal/LocationsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'contractors',
                name: 'contractors',
                component: () => import('@/views/portal/ContractorsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'credit-management',
                name: 'credit-management',
                component: () => import('@/views/portal/CreditManagementView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'departments',
                name: 'departments',
                component: () => import('@/views/portal/DepartmentsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'roles',
                name: 'roles',
                component: () => import('@/views/portal/EmployeeRolesView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site',
                name: 'site',
                component: () => import('@/views/portal/SiteView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'sitegroups',
                name: 'sitegroups',
                component: () => import('@/views/portal/SiteGroupsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'training-courses',
                name: 'training-courses',
                component: () => import('@/views/portal/TrainingCoursesView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'total-workforce-pool',
                name: 'total-workforce-pool',
                component: () => import('@/views/portal/TotalWorkForceView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'total-workforce-pool-create-account',
                name: 'total-workforce-pool-create-account',
                component: () => import('@/views/portal/TotalWorkForcePoolCreateAccount.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'workshifts',
                name: 'workshifts',
                component: () => import('@/views/portal/WorkShiftsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'payment-success',
                name: 'payment-success',
                component: () => import('@/views/portal/PaymentSuccessView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'payment-cancel',
                name: 'payment-cancel',
                component: () => import('@/views/portal/PaymentCancelView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'welcome',
                name: 'welcome',
                component: () => import('@/views/portal/WelcomeView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High'], requiredUserType: ['CompanyPortal'] }
            },
            // MEDIUM ACCESS ROUTES
            {
                path: 'site-dashboard',
                name: 'site-dashboard',
                component: () => import('@/views/portal/SiteDashboardView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site-workforce-pool',
                name: 'site-workforce-pool',
                component: () => import('@/views/portal/SiteWorkForceView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site-active-shifts',
                name: 'site-active-shifts',
                component: () => import('@/views/portal/SiteActiveShiftsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site-dtr-logs',
                name: 'site-dtr-logs',
                component: () => import('@/views/portal/SiteDtrLogsView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site-payroll-report',
                name: 'site-payroll-report',
                component: () => import('@/views/portal/SitePayrollReport.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'site-validation-issues',
                name: 'site-validation-issues',
                component: () => import('@/views/portal/SiteValidationIssues.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            },
            {
                path: 'user-guide',
                name: 'user-guide',
                component: () => import('@/views/portal/UserGuideView.vue'),
                meta: { requiresAuth: true, requiredAccessLevel: ['High', 'Medium'], requiredUserType: ['CompanyPortal'] }
            }
        ],
    },
    {
        path: '/:pathMatch(.*)*', // Catch-all route for 404
        name: 'not-found',
        component: NotFoundView,
        meta: { requiresAuth: false }
    },
];


const router = createRouter({
    history: createWebHistory('/'),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        return { top: 0 };
    }
});

router.onError((error) => {
    console.error('Router error:', error);
    if (error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.reload();
    }
});


router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // --- Root path ---
    if (to.path === '/') return true;

    // --- Public routes ---
    const isPublic = to.matched.some((r) => r.meta.requiresAuth === false);
    if (isPublic) {
        if (auth.user && (to.name === 'login' || to.name === 'signup')) {
            return { name: 'dashboard' };
        }
        return true;
    }

    // --- Auth check ---
    const isAuthenticated = auth.user ? true : await auth.fetchUser();

    if (!isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    // --- Normalize accessLevel casing ---
    const userAccess = auth.user?.accessLevel || auth.user?.AccessLevel;

    // --- Medium access redirect ---
    if (userAccess === 'Medium' && to.name === 'dashboard') {
        return { name: 'site-dashboard' };
    }

    // --- Collect meta requirements (handles nested routes properly) ---
    const requiredAccess = to.matched.flatMap(r => r.meta.requiredAccessLevel || []);
    const requiredUserType = to.matched.flatMap(r => r.meta.requiredUserType || []);

    // --- Access level check ---
    if (requiredAccess.length && !requiredAccess.includes(userAccess)) {
        return {
            name: to.path.startsWith('/kiosk')
                ? 'kiosk-invalid-access-level'
                : 'invalid-access-level'
        };
    }

    // --- User type check ---
    if (
        requiredUserType.length &&
        (!auth.user?.userType || !requiredUserType.includes(auth.user.userType))
    ) {
        return {
            name: to.path.startsWith('/kiosk')
                ? 'kiosk-invalid-access-level'
                : 'invalid-access-level'
        };
    }

    return true;
});

// const { posthog } = usePostHog()
export default router;